import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import { groupByApp } from "@/components/personal-apps/grouping";
import { useMyApps } from "@/components/personal-apps/myAppsStore";
import { iconImageUrl } from "@/components/personal-apps/computeClient";
import { appLinkProps } from "@/components/personal-apps/openApp";
import { AppIcon } from "@/components/personal-apps/AppIcon";
import type { AppGroup } from "@/components/personal-apps/types";
import { useSearch } from "@/components/search/SearchProvider";
import { discoverClient, type DiscoverApp } from "./discoverClient";
import { detailsNavigationTarget } from "./discovery/discoveryUrl";

/**
 * ⌘K / Ctrl+K searches cached My Apps immediately and the public Discover
 * catalogue after a debounce. Saved apps resume in a new tab; Discover
 * results open details without admitting a Run or installing an app.
 */

function candidateNames(group: AppGroup): string[] {
  const names = new Set<string>();
  if (group.display_name) names.add(group.display_name);
  for (const instance of group.instances) {
    if (instance.display_name) names.add(instance.display_name);
    if (instance.default_display_name) names.add(instance.default_display_name);
  }
  return [...names];
}

function matchScore(name: string, query: string): number | null {
  const target = name.toLowerCase();
  if (target === query) return 0;
  if (target.startsWith(query)) return 1;
  const words = target.split(/[\s\-_./]+/u).filter(Boolean);
  if (words.some((word) => word.startsWith(query))) return 2;
  if (target.includes(query)) return 3;
  return null;
}

/**
 * Local rank: exact > prefix > word-prefix > substring. Ties keep the
 * `groupByApp()` order (most recently used first).
 */
export function searchMyAppGroups(
  groups: AppGroup[],
  query: string,
): AppGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;
  const scored: { group: AppGroup; score: number; index: number }[] = [];
  groups.forEach((group, index) => {
    let best: number | null = null;
    for (const name of candidateNames(group)) {
      const score = matchScore(name, q);
      if (score !== null && (best === null || score < best)) best = score;
    }
    if (best !== null) scored.push({ group, score: best, index });
  });
  scored.sort((a, b) => a.score - b.score || a.index - b.index);
  return scored.map((entry) => entry.group);
}

type LauncherCandidate =
  | { kind: "my-app"; id: string; group: AppGroup }
  | { kind: "discover"; id: string; app: DiscoverApp };

export default function MyAppsLauncher({ signedIn, onNavigate }: {
  signedIn: boolean;
  onNavigate?: (route: string) => void;
}) {
  useLocale();
  const { closePalette } = useSearch();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [discover, setDiscover] = useState<{ query: string; apps: DiscoverApp[] } | null>(null);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<Element | null>(null);

  const cache = useMyApps();
  const groups = useMemo<AppGroup[]>(
    () => (cache.instances ? groupByApp(cache.instances) : []),
    [cache.instances],
  );
  const loading = cache.instances === null;
  const localCandidates = useMemo(
    () => signedIn ? searchMyAppGroups(groups, query) : [],
    [groups, query, signedIn],
  );
  const candidates = useMemo<LauncherCandidate[]>(() => [
    ...localCandidates.map(group => ({ kind: "my-app" as const, id: group.compute_id, group })),
    ...(discover?.query === query.trim() ? discover.apps : []).map(app => ({ kind: "discover" as const, id: `discover-${app.id}`, app })),
  ], [localCandidates, discover, query]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const requestedQuery = query.trim();
    setDiscoverLoading(true);
    setDiscoverError(null);
    const timer = window.setTimeout(() => {
      void discoverClient.page({ q: requestedQuery || undefined, limit: 12 }, controller.signal)
        .then(result => {
          if (!controller.signal.aborted) setDiscover({ query: requestedQuery, apps: result.apps });
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setDiscover(null);
            setDiscoverError("Couldn’t load apps.");
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setDiscoverLoading(false);
        });
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [open, query]);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  // One shortcut owner prevents two competing search dialogs/focus traps.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (e.repeat) return;
        if (open) {
          setOpen(false);
          return;
        }
        // A command surface must replace the other modal, not stack on it:
        // overlapping focus traps would otherwise fight over inert/scroll.
        closePalette();
        setQuery("");
        setDiscover(null);
        setActiveIndex(0);
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closePalette, open]);

  // Open/close lifecycle mirrors the Discover palette contract: focus in,
  // focus restore, inert background, body scroll lock.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement;
    const rootEl = document.getElementById("root");
    rootEl?.setAttribute("inert", "");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();
    return () => {
      rootEl?.removeAttribute("inert");
      document.body.style.overflow = prevOverflow;
      const el = restoreFocusRef.current;
      if (el instanceof HTMLElement && document.contains(el)) el.focus();
    };
  }, [open]);

  // Keep the highlight on a real row as the candidate list changes. A fresh
  // query always restarts at the top so ⌘K → type → Enter just works.
  useEffect(() => {
    setActiveIndex((i) =>
      Math.min(Math.max(i, 0), Math.max(candidates.length - 1, 0)),
    );
  }, [candidates.length]);

  const scrollActiveIntoView = useCallback((index: number) => {
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-launcher-index="${index}"]`,
    );
    try {
      el?.scrollIntoView({ block: "nearest" });
    } catch {
      // jsdom has no scrollIntoView — keyboard nav still works there.
    }
  }, []);

  const moveActive = useCallback(
    (next: number) => {
      if (candidates.length === 0) return;
      const clamped = Math.max(0, Math.min(next, candidates.length - 1));
      setActiveIndex(clamped);
      scrollActiveIntoView(clamped);
    },
    [candidates.length, scrollActiveIntoView],
  );

  const activateIndex = useCallback(
    (index: number) => {
      const anchor = listRef.current?.querySelector<HTMLAnchorElement>(
        `[data-launcher-index="${index}"]`,
      );
      // The row IS the link (`appLinkProps`), so keyboard activation keeps
      // the same bookmark/new-tab semantics as clicking it.
      anchor?.click();
    },
    [],
  );

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (candidates.length === 0) return;
      moveActive(activeIndex + 1 > candidates.length - 1 ? 0 : activeIndex + 1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (candidates.length === 0) return;
      moveActive(activeIndex - 1 < 0 ? candidates.length - 1 : activeIndex - 1);
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      moveActive(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      moveActive(candidates.length - 1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (candidates.length === 0) return;
      activateIndex(activeIndex);
    }
  };

  const onDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== "Tab") return;
    const focusables =
      dialogRef.current?.querySelectorAll<HTMLElement>("input, button, a[href]");
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  const trimmed = query.trim();
  const activeOptionId =
    candidates.length > 0
      ? `launcher-opt-${candidates[activeIndex]?.id ?? ""}`
      : undefined;

  return createPortal(
    <div
      className="palette-overlay"
      data-testid="my-apps-launcher"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={dialogRef}
        className="palette-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t("Search apps")}
        onKeyDown={onDialogKeyDown}
      >
        <div className="palette-input-row">
          <Search size={17} aria-hidden />
          <input
            ref={inputRef}
            type="text"
            className="palette-input"
            placeholder={t("Search apps")}
            aria-label={t("Search apps")}
            role="combobox"
            maxLength={200}
            aria-expanded="true"
            aria-controls="my-apps-launcher-results"
            aria-activedescendant={activeOptionId}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onInputKeyDown}
            enterKeyHint="search"
          />
          <button
            type="button"
            className="palette-close"
            onClick={close}
            aria-label={t("Close search")}
          >
            <X size={16} aria-hidden />
          </button>
        </div>
        <div className="palette-body">
          <div
            ref={listRef}
            id="my-apps-launcher-results"
            className="palette-results"
            role="listbox"
            aria-label={t("Search apps")}
          >
            {!signedIn ? (
              <p className="palette-empty"><Message message={"Sign in to search My Apps"} /></p>
            ) : loading ? (
              <p className="palette-empty"><Message message={"Loading your apps…"} /></p>
            ) : localCandidates.length === 0 ? (
              <p className="palette-empty">
                {t("My Apps")}: {" "}
                {trimmed
                  ? t("No apps match “{{value0}}”", { value0: trimmed })
                  : t("No apps yet.")}
              </p>
            ) : null}
            {candidates.map((candidate, index) => {
                const group = candidate.kind === "my-app" ? candidate.group : null;
                const publicApp = candidate.kind === "discover" ? candidate.app : null;
                const resume = group?.instances[0];
                if (group && !resume) return null;
                const href = publicApp ? detailsNavigationTarget({ kind: "discover", id: publicApp.id }) : resume!.app_url;
                const title = publicApp?.title ?? group!.display_name;
                return (
                  <a
                    key={candidate.id}
                    id={`launcher-opt-${candidate.id}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    data-launcher-index={index}
                    className={`palette-option ${index === activeIndex ? "palette-option--active" : ""}`}
                    {...(publicApp ? { href } : appLinkProps(href))}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={event => {
                      close();
                      if (publicApp && onNavigate && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
                        event.preventDefault();
                        onNavigate(href);
                      }
                    }}
                  >
                    <span className="palette-option-icon" aria-hidden>
                      <AppIcon
                        name={title}
                        seed={candidate.id}
                        size={28}
                        color={group?.icon_color}
                        glyph={group?.icon_glyph}
                        imageUrl={iconImageUrl(group?.icon_image_path ?? null)}
                        listingIconUrl={publicApp?.icon ?? group?.listing_icon_url}
                      />
                    </span>
                    <span className="palette-option-text">
                      <span className="palette-option-title">
                        {title}
                      </span>
                      <span className="palette-option-sub">{t(publicApp ? "Discover" : "My Apps")}</span>
                    </span>
                  </a>
                );
              })}
            {discoverLoading ? <p className="palette-empty" role="status"><Message message="Loading apps…" /></p> : null}
            {discoverError ? <p className="palette-empty" role="alert">{t("Discover")}: {t(discoverError)}</p> : null}
            {!discoverLoading && !discoverError && discover?.query === trimmed && discover.apps.length === 0
              ? <p className="palette-empty">{t("Discover")}: {t("No matching apps.")}</p> : null}
          </div>
        </div>
        <div className="palette-footer">
          <span><Message message={"↑↓ Select"} /></span>
          <span><Message message={"↵ Open"} /></span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
